import React, { useState, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import Helmet from 'react-helmet';
import { useMediaQuery } from '@mantine/hooks';
import { Tabs, Title, Text, MantineProvider, Button, TextInput, Paper } from '@mantine/core';
import '../styles/LocalPageStyles.css';
import { useScrollableTabs } from '../src/hooks/useScrollableTabs';

interface Area {
  name: string;
  slug: string;
  strip_area_suffix: boolean;
  events_count: number;
  skyline_image_url: string;
}

interface Country {
  id: number;
  name: string;
  flag_emoji: string;
  areas: Area[];
}

interface Continent {
  name: string;
  countries: Country[];
}

interface LocalPageProps {
  continents: Continent[];
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

const LocalPage: React.FC<LocalPageProps> = ({ continents, page_meta }) => {
  const formatAreaName = (name: string, stripSuffix: boolean): string => {
    return stripSuffix ? name.replace(/\s*Area$/i, '') : name;
  };

  const defaultTab = continents?.[0]?.name || '';
  const { tabListReference } = useScrollableTabs();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [expandedStates, setExpandedStates] = useState<Record<string, Record<string, boolean>>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const toggleExpand = (continentName: string, countryName: string) => {
    setExpandedStates((prev) => {
      const newState = { ...prev };
      if (!newState[continentName]) {
        newState[continentName] = {};
      }
      newState[continentName][countryName] = !prev[continentName]?.[countryName];
      return newState;
    });
  };

  const DEFAULT_VISIBLE_CITIES = 12;

  useEffect(() => {
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  return (
    <div
      style={{ backgroundColor: '#121212', color: '#f2f2f2', minHeight: '100vh', fontFamily: 'Poppins, sans-serif' }}
    >
      <MantineProvider>
        {page_meta && (
          <Helmet>
            <title>{page_meta.title}</title>
            <meta name="description" content={page_meta.description} />
            <meta property="og:title" content={page_meta.title} />
            <meta property="og:description" content={page_meta.description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={page_meta.url} />
            <meta property="og:image" content={page_meta.image} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={page_meta.title} />
            <meta name="twitter:description" content={page_meta.description} />
            <meta name="twitter:image" content={page_meta.image} />
          </Helmet>
        )}

        <Tabs
          variant="none"
          color="orange"
          className="country-container"
          value={activeTab}
          onChange={(tab) => {
            setActiveTab(tab ?? defaultTab);
            setSearchTerm('');
          }}
          mb="2rem"
        >
          <Tabs.List
            ref={tabListReference}
            style={{
              padding: '30px',
              display: 'flex',
              justifyContent: isMobile ? 'flex-start' : 'center',
              backgroundColor: '#75757520',
            }}
          >
            {continents.map((continent) => (
              <Tabs.Tab key={continent.name} value={continent.name} style={{ color: '#f2f2f2', minHeight: '50px' }}>
                {continent.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {continents.map((continent) => {
            const isSearching = searchTerm.trim().length > 0;
            const searchLower = searchTerm.toLowerCase();

            const visibleCountries = continent.countries
              .map((country) => {
                const filteredAreas = country.areas
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .filter((area) =>
                    formatAreaName(area.name, area.strip_area_suffix).toLowerCase().includes(searchLower),
                  );
                return {
                  ...country,
                  areas: filteredAreas,
                };
              })
              .filter((country) => country.areas.length > 0)
              .sort((a, b) => b.areas.length - a.areas.length);

            return (
              <Tabs.Panel key={continent.name} value={continent.name} pt="xs" mx="auto">
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#121212',
                    padding: isMobile ? '1rem 3vw' : '1rem 0rem',
                    zIndex: 10,
                    maxWidth: 800,
                    margin: '1rem auto',
                  }}
                >
                  <TextInput
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.currentTarget.value)}
                    placeholder="Search for a city..."
                    leftSection={
                      <span role="img" aria-label="search">
                        🔍
                      </span>
                    }
                    radius="md"
                    size="md"
                    styles={{
                      input: { backgroundColor: '#1e1e1e', color: '#f2f2f2', borderColor: '#EA580C' },
                    }}
                  />
                </div>

                {visibleCountries.length === 0 && (
                  <Paper
                    withBorder
                    p="lg"
                    mt="md"
                    radius="md"
                    shadow="sm"
                    style={{
                      backgroundColor: '#1e1e1e',
                      borderColor: '#EA580C',
                      color: '#f2f2f2',
                      maxWidth: 600,
                      margin: '2rem auto',
                    }}
                  >
                    <Title order={4} mb="xs">
                      No cities matched your search.
                    </Title>
                    <Text>
                      Are we missing a city that has events listed? We typically like to have at least 10 events to add
                      it. Email us using the question mark button in the lower corner and we’ll look into it!
                    </Text>
                  </Paper>
                )}

                {visibleCountries.map((country) => {
                  const sortedAreas = country.areas;
                  const hasMoreCities = sortedAreas.length > DEFAULT_VISIBLE_CITIES;
                  const isExpanded = !!expandedStates[continent.name]?.[country.name];
                  const visibleAreas =
                    isExpanded || isSearching ? sortedAreas : sortedAreas.slice(0, DEFAULT_VISIBLE_CITIES);
                  const countryKey = `${continent.name}-${country.name}-${country.id || ''}`;

                  return (
                    <div key={countryKey} className="country-card" style={{ margin: '0 auto' }}>
                      <Title order={3} className="poppins" mb="0">
                        <span className="flag">{country.flag_emoji}</span>
                        {country.name}
                      </Title>

                      <Text size="sm" c="dimmed" mb="md">
                        {country.areas.length} {country.areas.length === 1 ? 'city' : 'cities & counting'}
                      </Text>

                      <ul className="areas-grid">
                        {visibleAreas.map((area) => (
                          <li key={area.slug} className="area-card-item">
                            <Link
                              href={`/city/${area.slug}`}
                              className="area-link"
                              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', width: '100%' }}
                            >
                              <div className="area-circle">
                                {area.skyline_image_url ? (
                                  <img
                                    src={area.skyline_image_url}
                                    alt={formatAreaName(area.name, area.strip_area_suffix)}
                                    className="area-skyline-image"
                                  />
                                ) : (
                                  formatAreaName(area.name, area.strip_area_suffix).charAt(0)
                                )}
                              </div>
                              <div className="area-info">
                                <span className="area-name">{formatAreaName(area.name, area.strip_area_suffix)}</span>
                                <span className="events-count">
                                  {area.events_count} {area.events_count === 1 ? 'event' : 'events'}
                                  {area.events_count > 25 && <span className="hot-badge">🔥</span>}
                                </span>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>

                      {!isSearching && hasMoreCities && (
                        <Button
                          variant="subtle"
                          color="orange"
                          size="lg"
                          onClick={() => toggleExpand(continent.name, country.name)}
                          fullWidth
                          mt="sm"
                          style={{
                            color: '#f2f2f2',
                            backgroundColor: '#EA580C',
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          {isExpanded
                            ? `Show Less (${DEFAULT_VISIBLE_CITIES} of ${sortedAreas.length})`
                            : `Show All ${sortedAreas.length} Cities (${sortedAreas.length - DEFAULT_VISIBLE_CITIES} more)`}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </Tabs.Panel>
            );
          })}
        </Tabs>
      </MantineProvider>
    </div>
  );
};

export default LocalPage;
